import React, { useState } from "react";
import Icon from "../components/icons/Icon.jsx";
import Check from "../components/icons/Check.jsx";
import Arrow from "../components/icons/Arrow.jsx";
import { SERVICE_OPTIONS } from "../data/services.js";
import COMPANY from "../data/company.js";
import { submitEnquiry } from "../api/client.js";

const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "" };

const Contact = () => {
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(null);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (errs[k]) setErrs({ ...errs, [k]: null }); };

  const submit = async () => {
    const e = {};
    if (!f.name.trim()) e.name = "Enter your name so we know who to reply to.";
    if (!f.email.trim()) e.email = "Enter an email address for the quotation.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email)) e.email = "Check the email address — it looks incomplete.";
    if (!f.service) e.service = "Choose the service you need.";
    if (!f.message.trim()) e.message = "Tell us what needs moving, from where and by when.";
    setErrs(e);
    if (Object.keys(e).length > 0) return;

    setSending(true);
    setFailed(null);
    try {
      await submitEnquiry(f);
      setSent(true);
    } catch (err) {
      setFailed(err.message || "The enquiry did not send. Try again, or call the transport desk.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <section className="phero">
        <div className="wrap">
          <span className="eyebrow">Contact</span>
          <h1 className="h-xl" style={{ maxWidth: "15ch" }}>Get a transport quote</h1>
          <p className="lead" style={{ marginTop: 24 }}>Send the collection point, the destination and the
            deadline. A member of the transport team will come back to you — usually within the same working day.</p>
        </div>
      </section>

      <section className="sec-tight">
        <div className="wrap grid g2" style={{ gap: 48, alignItems: "start" }}>
          <div className="form reveal">
            {sent ? (
              <div>
                <div className="ok">
                  <span style={{ color: "var(--indigo)" }}><Check s={26} /></span>
                  <div>
                    <h3 style={{ marginBottom: 8 }}>Enquiry sent</h3>
                    <p className="muted" style={{ fontSize: ".95rem" }}>
                      Thanks {f.name.split(" ")[0]} — your enquiry is with the transport team. We will reply to{" "}
                      <strong style={{ color: "var(--navy)" }}>{f.email}</strong>, usually within the same working day.
                      If it is urgent, call the operations line and quote your company name.
                    </p>
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ marginTop: 24 }}
                        onClick={() => { setSent(false); setFailed(null); setF(EMPTY); }}>
                  Send another enquiry
                </button>
              </div>
            ) : (
              <div>
                <h2 className="h-md" style={{ marginBottom: 8 }}>Request a quotation</h2>
                <p className="muted" style={{ fontSize: ".93rem", marginBottom: 30 }}>Fields marked * are required.</p>

                <div className="grid g2" style={{ gap: 0, columnGap: 18 }}>
                  <div className="field">
                    <label htmlFor="c-name">Name *</label>
                    <input id="c-name" value={f.name} onChange={set("name")} className={errs.name ? "bad" : ""}
                           aria-invalid={!!errs.name} autoComplete="name" />
                    {errs.name && <span className="err">{errs.name}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="c-company">Company</label>
                    <input id="c-company" value={f.company} onChange={set("company")} autoComplete="organization" />
                  </div>
                  <div className="field">
                    <label htmlFor="c-email">Email *</label>
                    <input id="c-email" type="email" value={f.email} onChange={set("email")}
                           className={errs.email ? "bad" : ""} aria-invalid={!!errs.email} autoComplete="email" />
                    {errs.email && <span className="err">{errs.email}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="c-phone">Telephone</label>
                    <input id="c-phone" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="c-service">Service required *</label>
                  <select id="c-service" value={f.service} onChange={set("service")}
                          className={errs.service ? "bad" : ""} aria-invalid={!!errs.service}>
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errs.service && <span className="err">{errs.service}</span>}
                </div>

                <div className="field">
                  <label htmlFor="c-message">Message *</label>
                  <textarea id="c-message" value={f.message} onChange={set("message")}
                            className={errs.message ? "bad" : ""} aria-invalid={!!errs.message}
                            placeholder="Collection postcode, delivery postcode, what is moving, weight or pallet count, and the date it needs to arrive." />
                  {errs.message && <span className="err">{errs.message}</span>}
                </div>

                {failed && (
                  <p className="err" style={{ marginBottom: 16, display: "block" }}>{failed}</p>
                )}

                <button className="btn btn-primary" style={{ width: "100%" }}
                        onClick={submit} disabled={sending} aria-busy={sending}>
                  {sending ? "Sending enquiry…" : <>Send enquiry <Arrow /></>}
                </button>
                <p className="muted" style={{ fontSize: ".82rem", marginTop: 16 }}>
                  We use your details only to respond to this enquiry. Nothing is shared with third parties.
                </p>
              </div>
            )}
          </div>

          <div className="reveal" style={{ display: "grid", gap: 26 }}>
            <div className="map">
              <div className="map-grid" />
              <div className="map-pin">
                <div style={{ color: "var(--indigo)", display: "flex", justifyContent: "center" }}><Icon name="pin" s={34} /></div>
                <div style={{ fontFamily: "var(--display)", fontWeight: 700, marginTop: 8 }}>{COMPANY.name}</div>
                <div className="muted" style={{ fontSize: ".88rem" }}>{COMPANY.address.line1}, {COMPANY.address.city} {COMPANY.address.postcode}</div>
                <div className="net-title" style={{ marginTop: 12 }}>Google Maps embed placeholder</div>
              </div>
            </div>

            <div className="card" style={{ padding: 30 }}>
              <span className="eyebrow">Business information</span>
              <div className="info-row">
                <span className="ic"><Icon name="phone" s={20} /></span>
                <div><div className="k">Telephone</div><div className="v"><a href={COMPANY.phoneHref}>{COMPANY.phone}</a></div></div>
              </div>
              <div className="info-row">
                <span className="ic"><Icon name="mail" s={20} /></span>
                <div><div className="k">Email</div><div className="v"><a href={COMPANY.emailHref}>{COMPANY.email}</a></div></div>
              </div>
              <div className="info-row">
                <span className="ic"><Icon name="pin" s={20} /></span>
                <div><div className="k">Registered office</div>
                  <div className="v">{COMPANY.name}<br />{COMPANY.address.line1}<br />{COMPANY.address.city} {COMPANY.address.postcode}</div></div>
              </div>
              <div className="info-row">
                <span className="ic"><Icon name="clock" s={20} /></span>
                <div><div className="k">Operating hours</div>
                  <div className="v">Office: {COMPANY.hours.office}<br />Transport desk: {COMPANY.hours.transportDesk}</div></div>
              </div>
            </div>

            <div className="card" style={{ padding: 30, background: "var(--indigo-soft)", borderColor: "var(--indigo-line)" }}>
              <span className="eyebrow">Operating area</span>
              <h3 className="h-md" style={{ fontSize: "1.15rem", marginBottom: 12 }}>United Kingdom &amp; Ireland</h3>
              <p className="muted" style={{ fontSize: ".94rem" }}>England, Scotland, Wales, Northern Ireland and the
                Republic of Ireland — including cross-border and ferry-linked movements, handled end to end by one operator.</p>
              <div className="svc-list" style={{ borderColor: "var(--indigo-line)" }}>
                {["England", "Scotland", "Wales", "Northern Ireland", "Republic of Ireland"].map((t) => (
                  <span className="tag" key={t} style={{ background: "#fff" }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
